import React from 'react';
import { Typography, TypographyProps, SxProps } from '@mui/material';
import { MobileSpacing } from './MobileResponsiveSpacing';

interface MobileTypographyProps extends Omit<TypographyProps, 'variant'> {
  variant: 'label' | 'body' | 'title';
  children: React.ReactNode;
  sx?: SxProps;
}

/**
 * MobileTypography - Reusable typography component with mobile settings styling
 * Extracted from repeated typography patterns across mobile settings files
 */
function MobileTypography({ variant, children, sx, ...props }: MobileTypographyProps): JSX.Element {
  const baseStyles = {
    label: {
      color: 'text.secondary',
      fontSize: MobileSpacing.fontSizeSmall,
      fontWeight: 600,
      mb: MobileSpacing.marginBottom,
    },
    body: {
      color: 'text.primary',
      fontSize: MobileSpacing.fontSizeLarge,
    },
    title: {
      color: 'text.primary',
      fontSize: MobileSpacing.fontSizeXLarge,
      fontWeight: 500,
    },
  };

  // Handle sx merging properly for arrays, functions, and objects
  const getMergedSx = (): SxProps => {
    if (!sx) {
      return baseStyles[variant];
    }

    // If sx is an array, prepend baseStyles[variant]
    if (Array.isArray(sx)) {
      return [baseStyles[variant], ...sx];
    }

    // If sx is a function, compose it with baseStyles[variant]
    if (typeof sx === 'function') {
      return (theme) => {
        return sx(theme);
      };
    }

    // If sx is boolean or other primitive, wrap in array
    if (typeof sx !== 'object' || sx === null) {
      return [baseStyles[variant], sx];
    }

    // If sx is an object, merge with baseStyles[variant]
    return { ...baseStyles[variant], ...sx };
  };

  return (
    <Typography {...props} sx={getMergedSx()}>
      {children}
    </Typography>
  );
}

export default MobileTypography;
